import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { FiBookOpen, FiCheck, FiArrowRight, FiUsers, FiBook, FiTarget, FiAward } from 'react-icons/fi'

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
}

const Training = () => {
  const [headerRef, headerInView] = useInView({ threshold: 0.1, triggerOnce: true })
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1, triggerOnce: true })
  const [approachRef, approachInView] = useInView({ threshold: 0.1, triggerOnce: true })
  const [ctaRef, ctaInView] = useInView({ threshold: 0.1, triggerOnce: true })

  const trainingPrograms = [
    {
      icon: <FiUsers className="w-8 h-8" />,
      title: "Leadership Development",
      description: "Comprehensive programs to develop strong leaders at all levels of your organization.",
      features: [
        "Executive coaching",
        "Team leadership training",
        "Strategic thinking workshops",
        "Communication skills development"
      ]
    },
    {
      icon: <FiBook className="w-8 h-8" />,
      title: "Technical Skills Training",
      description: "Industry-specific technical training to keep your team up-to-date with the latest technologies.",
      features: [
        "Software development skills",
        "Digital transformation training",
        "Cloud computing workshops",
        "Cybersecurity awareness"
      ]
    },
    {
      icon: <FiTarget className="w-8 h-8" />,
      title: "Professional Development",
      description: "Programs designed to enhance professional skills and career growth.",
      features: [
        "Project management",
        "Business analysis",
        "Agile methodologies",
        "Customer service excellence"
      ]
    },
    {
      icon: <FiAward className="w-8 h-8" />,
      title: "Compliance & Safety",
      description: "Essential training programs to ensure workplace compliance and safety.",
      features: [
        "Workplace safety",
        "HR compliance",
        "Data protection",
        "Ethics and conduct"
      ]
    }
  ]

  return (
    <div className="pt-20">
      {/* Header Section */}
      <section className="bg-primary-600 text-white py-16 md:py-24">
        <div className="container">
          <motion.div 
            ref={headerRef}
            initial="hidden"
            animate={headerInView ? "visible" : "hidden"}
            variants={staggerContainer}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.h1 variants={fadeIn} className="text-4xl md:text-5xl font-bold mb-6">
              Training Services
            </motion.h1>
            <motion.p variants={fadeIn} className="text-lg md:text-xl text-white/80 mb-8">
              Comprehensive training solutions to enhance your team's capabilities
            </motion.p>
          </motion.div>
        </div>
      </section>

      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-bold mb-4">Corporate Training Services</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Comprehensive training solutions to develop your workforce and drive organizational success.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {trainingPrograms.map((program, index) => (
            <motion.div
              key={program.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-lg p-6"
            >
              <div className="text-primary-600 mb-4">
                {program.icon}
              </div>
              <h3 className="text-xl font-bold mb-3">{program.title}</h3>
              <p className="text-gray-600 mb-4">{program.description}</p>
              <ul className="space-y-2">
                {program.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-primary-600 text-white rounded-lg p-8 text-center"
        >
          <h2 className="text-2xl font-bold mb-4">Ready to Transform Your Workforce?</h2>
          <p className="text-white/80 mb-6">
            Let's discuss how our training programs can help your organization achieve its goals.
          </p>
          <button className="btn btn-accent">
            Schedule a Consultation
          </button>
        </motion.div>
      </div>
    </div>
  )
}

export default Training